import {
	MicrosoftTeamsSDK
} from '../ms-teams-sdk';
import {
	transformGraphUser
} from '../ms-teams-sdk/util/MsTeamsGraphUserUtil';
import {
	ISearchContactsAdapter,
	ISearchContactsAdapterSearchInput,
	ISearchContactsAdapterSearchResponse,
	IWebexIntContact
} from '../packages/adapter-interfaces/src';
import { IGraphUser } from '../ms-teams-sdk/service/MsTeamsUserService';
import { isGraphDirectoryForbidden } from '../ms-teams-sdk/service/graphDirectoryAccess';
import { isPhoneOrExtensionQuery, transformDirectoryUsersToContacts } from './DirectorySearchAdapterHelper';

enum SourceNames {
	OUTLOOK = 'Outlook contacts',
		DIRECTORY = 'Directory contacts',
		DEVICES = 'Devices',
}

enum SourceTypes {
	OUTLOOK = 'outlook',
		DIRECTORY = 'directory',
}

export class MsTeamsSearchContactsAdapter implements ISearchContactsAdapter {
	getSources(): string[] {
		// Directory and Outlook stay first. Speed dial reads only those two sections.
		return [SourceNames.DIRECTORY, SourceNames.OUTLOOK, SourceNames.DEVICES];
	}

    getSourceType(contactId: string): SourceTypes {
        // use id length to detect if the contact is directory contact or outlook contact
        return contactId.length == 36 ? SourceTypes.DIRECTORY : SourceTypes.OUTLOOK;
    }

	async search(
		criteria: ISearchContactsAdapterSearchInput
	): Promise < ISearchContactsAdapterSearchResponse > {
		try {
			const isPhoneOrExtension = isPhoneOrExtensionQuery(criteria.searchText?.trim() ?? '');
			const directoryOptions = criteria.preferGraphForDirectory ? { preferGraphForDirectory: true as const } : undefined;
			const skipOutlookContacts = isGraphDirectoryForbidden();
			// Phone/extension: directory only (DSS). Skip Outlook/Graph to avoid unnecessary API calls.
			const [directoryUsers, contacts] = isPhoneOrExtension
				? [
					await MicrosoftTeamsSDK.user.fetchDirectoryUsersEnhanced(criteria.searchText, undefined, directoryOptions),
					[] as IGraphUser[],
				]
				: await Promise.all([
					MicrosoftTeamsSDK.user.fetchDirectoryUsersEnhanced(criteria.searchText, undefined, directoryOptions),
					skipOutlookContacts
						? Promise.resolve([] as IGraphUser[])
						: MicrosoftTeamsSDK.contact.fetchContactsEnhanced(criteria.searchText),
				]);
			const fetchDirectoryAvatar = (id: string) => MicrosoftTeamsSDK.user.fetchDirectoryUserAvatar(id);
			const people = (directoryUsers ?? []).filter((user) => !user.isWorkspaceDevice);
			const devices = (directoryUsers ?? []).filter((user) => user.isWorkspaceDevice);
			return {
				count: people.length + devices.length + (contacts?.length ?? 0),
				items: {
					[SourceNames.DIRECTORY]: transformDirectoryUsersToContacts(people, fetchDirectoryAvatar),
					[SourceNames.OUTLOOK]: contacts.map((graphUser) =>
						transformGraphUser(graphUser, () =>
							MicrosoftTeamsSDK.contact.fetchContactAvatar(graphUser.id)
						)
					),
					[SourceNames.DEVICES]: transformDirectoryUsersToContacts(devices, fetchDirectoryAvatar),
				},
			};
		} catch (error) {
			console.error('Error in MsTeamsSearchContactsAdapter.search:', error);
			return {
				count: 0,
				items: {
					[SourceNames.DIRECTORY]: [],
					[SourceNames.OUTLOOK]: [],
					[SourceNames.DEVICES]: [],
				},
			};
		}
	}

	async getOutlookContact(contactId: string): Promise<IWebexIntContact | void> {
		const graphUser = MicrosoftTeamsSDK.contact.getContact(contactId);
		if (graphUser != undefined) {
			return graphUser.then((graphUser) => {
				if (graphUser != undefined) {
					return transformGraphUser(graphUser, () =>
						MicrosoftTeamsSDK.contact.fetchContactAvatar(graphUser.id)
					)
				}
			}
			);

		}
	}

    async getDirectoryContact(userId: string): Promise<IWebexIntContact | void>  {
        const graphUser = MicrosoftTeamsSDK.user.getUserByID(userId);
        if (graphUser != undefined) {
            return graphUser.then((graphUser) =>
                transformGraphUser(graphUser, () =>
                    MicrosoftTeamsSDK.user.fetchDirectoryUserAvatar(graphUser.id)
                ));
        }
    }

    async getContactDetailById(userId: string): Promise<IWebexIntContact | void>  {
        if (this.getSourceType(userId) == SourceTypes.OUTLOOK) {
            return this.getOutlookContact(userId);
        }
        return this.getDirectoryContact(userId);
    }

	async getUsersByIds(userIds: string[]): Promise<IWebexIntContact[]> {
		const [directoryIds, outlookIds] = userIds.reduce(
			([dirIds, outIds], id) => {
				const type = this.getSourceType(id);
				return type === SourceTypes.DIRECTORY
					? [[...dirIds, id], outIds]
					: [dirIds, [...outIds, id]];
			},
			[[], []] as [string[], string[]]
		);

		const [contactUsers, directoryUsers] = await Promise.all([
			this.getFilterOutLookContacts(outlookIds),
			this.getDirectoryContacts(directoryIds),
		]);

		const bothOutlookandDirectoryUsers = [...contactUsers, ...directoryUsers].filter(Boolean);

		return bothOutlookandDirectoryUsers;

	}

	async getDirectoryContacts(userIds: string[]): Promise<IWebexIntContact[]> {
		const users = await MicrosoftTeamsSDK.user.getUsersByIds(userIds);
		return users.map((graphUser) =>
			transformGraphUser(graphUser, () =>
				MicrosoftTeamsSDK.user.fetchDirectoryUserAvatar(graphUser.id)
			)
		);
	}

	async getFilterOutLookContacts(outLookIds: string[]): Promise<IWebexIntContact[]> {
		const finalList: IWebexIntContact[] = [];
		if (outLookIds.length > 0) {
			// Load all contacts
			const allContacts = await MicrosoftTeamsSDK.contact.listAllContacts();
			const userMap: Record<string, IGraphUser> = {};

			// Create a map of contacts by their id
			for (const contact of allContacts) {
				userMap[contact.id] = contact;
			}

			for (const contactId of outLookIds) {
				if (userMap[contactId]) {
					const contact = userMap[contactId];
					const transformedContact = transformGraphUser(
						contact,
						async () => {
							try {
								return await MicrosoftTeamsSDK.contact.fetchContactAvatar(contact.id);
							} catch (error) {
								// Avatar fetch failed - return undefined to use default avatar
								console.log(`SD: Avatar fetch failed for contact ${contact.id}:`, error);
								return undefined;
							}
						}
					);
					finalList.push(transformedContact);
				} else {
					try {
						const outlookContact = await this.getOutlookContact(contactId);
						if (outlookContact) {
							finalList.push(outlookContact);
						}
					} catch (error) {
						// Individual contact lookup failed - log but don't affect other contacts
						console.log(`SD: Individual contact lookup failed for ID ${contactId}:`, error);
						// Skip this contact and continue with others
					}
				}
			}
		}

		return finalList;

	}
}